/**
 * Cost/token estimator for CSV column matching.
 *
 * The JR GenAI gateway reports `prompt_tokens` / `completion_tokens` as *credits*,
 * not raw tokens. This mirrors the AiChatBot cost-tracker to turn those credits
 * into an estimated token/USD figure for the diagnostic log (see log.ts —
 * dev-only or `?csvDebug`). Kept local (not imported from the AiChatBot engine)
 * because matching is a single stateless request. Feeds logging only; nothing
 * user-facing consumes it.
 */

import type { MatchingModelId } from './layer-ai'

// (inputCredits + outputCredits) * CREDIT_TO_USD = USD. Same constant as the gateway.
const CREDIT_TO_USD = 0.0000165

// Tokens-per-credit factors by model id (from the gen-ai-service model table).
// Higher factor = cheaper per credit.
const MODEL_CREDIT_FACTORS: Record<
  MatchingModelId,
  { input: number; output: number }
> = {
  'jr-auto/fast': { input: 15, output: 3 },
  'jr-auto/standard': { input: 5, output: 1 },
  'jr-auto/powerful': { input: 3, output: 0.6 },
  'aws/deepseek.v3.2': { input: 22.3, output: 7.4 },
}

export interface MatchUsage {
  model: MatchingModelId
  /** Gateway credits (what the stream reports as prompt/completion tokens). */
  inputCredits: number
  outputCredits: number
}

export interface MatchCost {
  model: MatchingModelId
  inputCredits: number
  outputCredits: number
  /** Estimated real tokens = credits × model factor. */
  inputTokens: number
  outputTokens: number
  usd: number
}

export function estimateMatchCost(usage: MatchUsage): MatchCost {
  const factors = MODEL_CREDIT_FACTORS[usage.model] ?? { input: 1, output: 1 }
  return {
    model: usage.model,
    inputCredits: usage.inputCredits,
    outputCredits: usage.outputCredits,
    inputTokens: Math.round(usage.inputCredits * factors.input),
    outputTokens: Math.round(usage.outputCredits * factors.output),
    usd: (usage.inputCredits + usage.outputCredits) * CREDIT_TO_USD,
  }
}
